import { Controller } from 'stimulus';

// The task's equipment window (Tasks::EquipmentWindow, tasks/equipment/*):
// Traceability Only (or the one Equipment section) and, with the new
// Schedule, On the Schedule (tasks/equipment/_booking), every list drawn the
// same way: a row per piece with its calibration and a ×, and a search box
// to add more (for: trace, book, any), what's usually used on this test
// first. This Equipment rows carry Holds / Can Share and Move to Traceability
// Only; Any of These takes pieces and whole kinds; a Traceability piece that
// can be booked has Book It. An empty Traceability Only folds to one line
// until + Add. On the Schedule's searches come soonest first, each piece
// saying when the task could start on it (the window's ranks, from the
// Schedule's trial plans). Nothing saves here: the rows' inputs post with the
// task form, the Tasks tab pop-up's Save or the task page's Save Equipment.
export default class extends Controller {
  static targets = ['traceRows', 'traceRow', 'traceTemplate', 'traceEmpty', 'traceSection', 'traceFold', 'query', 'results', 'booking',
    'bookedRows', 'bookedRow', 'bookedTemplate', 'bookedEmpty', 'choiceRows', 'choiceRow', 'choiceTemplate', 'kindTemplate', 'choiceEmpty']

  connect() {
    this.pieces = {};
    try {
      JSON.parse(this.data.get('pieces') || '[]').forEach((piece) => { this.pieces[piece.id] = piece; });
    } catch (_) { /* drawn from the page instead */ }
    try {
      this.ranks = JSON.parse(this.data.get('ranks') || '{}');
    } catch (_) { this.ranks = {}; }
    this.started = this.data.get('started') === 'true';
    this.template = this.data.get('template') === 'true';
    this.items = [];
    this.highlight = -1;
    this.activeFor = 'trace';
    this.boundModeClick = this.modeClicked.bind(this);
    this.element.addEventListener('click', this.boundModeClick);
    this.syncMode();
    this.syncEmpty();
  }

  disconnect() {
    clearTimeout(this.timer);
    clearTimeout(this.blurTimer);
    if (this.request) this.request.abort();
    this.element.removeEventListener('click', this.boundModeClick);
  }

  // ----- search (one per list: trace, book, any) -----

  // On the Schedule's lists (book, any) come with when the task could start
  // on each piece; Traceability Only's doesn't book, so it has none
  ranked() {
    return this.activeFor !== 'trace' && Object.keys(this.ranks).length > 0;
  }

  rankOrder() {
    return Object.keys(this.ranks).sort((a, b) => this.ranks[a].o - this.ranks[b].o);
  }

  // A whole kind's soonest piece
  soonestOf(kindId) {
    let best = null;
    Object.keys(this.ranks).forEach((id) => {
      const rank = this.ranks[id];
      if (String(rank.k) === String(kindId) && (!best || rank.o < best.o)) best = rank;
    });
    return best;
  }

  toneClass(tone) {
    if (tone === 'busy-then') return ' is-busy';
    return ['away-then', 'down-then', 'no-room'].includes(tone) ? ' is-bad' : '';
  }

  search(event) {
    if (event && event.currentTarget && event.currentTarget.dataset.for) this.activeFor = event.currentTarget.dataset.for;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.fetchResults(), 150);
  }

  activeQuery() {
    return this.queryTargets.find((q) => q.dataset.for === this.activeFor) || this.queryTargets[0];
  }

  activeResults() {
    return this.resultsTargets.find((r) => r.dataset.for === this.activeFor) || this.resultsTargets[0];
  }

  fetchResults() {
    const query = this.activeQuery();
    if (!query) return;
    const url = new URL(this.data.get('searchUrl'), window.location.origin);
    url.searchParams.set('q', query.value.trim());
    url.searchParams.set('for', this.activeFor);
    const exclude = this.activeFor === 'any' ? this.choiceIds() : this.currentIds();
    exclude.forEach((id) => url.searchParams.append('exclude[]', id));
    if (this.activeFor === 'any') this.kindIds().forEach((id) => url.searchParams.append('exclude_kinds[]', id));
    if (this.ranked()) url.searchParams.set('rank', this.rankOrder().join(','));
    const name = this.taskName();
    if (name) url.searchParams.set('name', name);
    if (this.request) this.request.abort();
    this.request = new AbortController();
    const forList = this.activeFor;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: this.request.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => { if (json && forList === this.activeFor) this.showResults(json); })
      .catch(() => { /* aborted by the next keystroke, or offline */ });
  }

  showResults(json) {
    const box = this.activeResults();
    box.innerHTML = '';
    this.items = [];
    const query = this.activeQuery().value.trim();
    const group = (label, list, button) => {
      if (!list || !list.length) return;
      box.appendChild(this.el('div', 'te-results-h', label));
      list.forEach((entry) => box.appendChild(button(entry)));
    };
    const pieceButton = (piece) => { this.pieces[piece.id] = piece; return this.resultButton(piece); };
    group(`Usually used on ${json.name || 'this test'}`, json.usual, pieceButton);
    group(this.activeFor === 'trace' ? 'All equipment' : 'On the Schedule', json.rest, pieceButton);
    if (json.more > 0) box.appendChild(this.el('div', 'te-results-more', `${json.more} more: keep typing`));
    group('Every piece of a kind', json.kinds, (kind) => this.kindButton(kind));
    if (!this.items.length) {
      const none = this.activeFor === 'trace' ? 'Everything is already on this task' : 'Nothing else on the Schedule';
      box.appendChild(this.el('div', 'te-results-none', query ? `Nothing matches "${query}"` : none));
    }
    box.hidden = false;
    this.highlight = this.items.length ? 0 : -1;
    this.paint();
  }

  resultButton(piece) {
    const button = this.el('button', 'te-result');
    button.type = 'button';
    button.setAttribute('role', 'option');
    button.dataset.id = piece.id;
    const first = this.el('span', 'te-result-l1');
    if (piece.ident) first.appendChild(this.el('b', 'te-ident', piece.ident));
    first.appendChild(document.createTextNode(piece.name));
    if (piece.bookable && this.twoSections() && this.activeFor === 'trace') first.appendChild(this.el('em', 'te-sched', 'Schedule'));
    button.appendChild(first);
    const second = this.el('span', `te-result-l2${piece.cal_bad ? ' te-bad' : ''}`);
    second.textContent = [piece.category, piece.location, this.template ? null : piece.cal].filter(Boolean).join(' · ');
    button.appendChild(second);
    const rank = this.ranked() && this.ranks[piece.id];
    if (rank) button.appendChild(this.el('span', `te-when${this.toneClass(rank.t)}`, rank.w));
    // mousedown, so the search box keeps its focus for the next one
    button.addEventListener('mousedown', (event) => { event.preventDefault(); this.pick(piece.id); });
    this.items.push(button);
    return button;
  }

  kindButton(kind) {
    const button = this.el('button', 'te-result');
    button.type = 'button';
    button.setAttribute('role', 'option');
    button.dataset.kind = kind.id;
    const first = this.el('span', 'te-result-l1');
    first.appendChild(this.el('span', 'te-kind', 'All'));
    first.appendChild(document.createTextNode(` Every ${kind.name}`));
    button.appendChild(first);
    button.appendChild(this.el('span', 'te-result-l2', `${this.plural(kind.count, 'piece')} on the Schedule`));
    const soonest = this.ranked() && this.soonestOf(kind.id);
    if (soonest) button.appendChild(this.el('span', 'te-when', soonest.d ? `Soonest ${soonest.d}` : soonest.w));
    button.addEventListener('mousedown', (event) => { event.preventDefault(); this.pickKind(kind); });
    this.items.push(button);
    return button;
  }

  searchKey(event) {
    const box = this.activeResults();
    const open = box && !box.hidden;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!open || !this.items.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.highlight = (this.highlight + step + this.items.length) % this.items.length;
      this.paint();
    } else if (event.key === 'Enter') {
      // Never submits the form (or the pop-up) from the search box
      event.preventDefault();
      event.stopPropagation();
      const item = open && this.highlight >= 0 && this.items[this.highlight];
      if (item) item.dispatchEvent(new MouseEvent('mousedown', { cancelable: true }));
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      box.hidden = true;
    }
  }

  searchBlur() {
    clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => this.resultsTargets.forEach((box) => { box.hidden = true; }), 150);
  }

  // Keeps the highlighted result in view by scrolling the list alone, never
  // the window around it.
  paint() {
    this.items.forEach((item, index) => item.classList.toggle('is-hl', index === this.highlight));
    const current = this.items[this.highlight];
    const box = this.activeResults();
    if (!current || !box) return;
    if (current.offsetTop < box.scrollTop) {
      box.scrollTop = current.offsetTop;
    } else if (current.offsetTop + current.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = current.offsetTop + current.offsetHeight - box.clientHeight;
    }
  }

  pick(id) {
    const piece = this.pieces[id];
    if (!piece) return;
    if (this.activeFor === 'any') {
      if (this.choiceIds().includes(String(id))) return;
      this.choiceRowsTarget.appendChild(this.choiceRowFor(piece));
    } else {
      if (this.currentIds().includes(String(id))) return;
      if (this.activeFor === 'book') this.bookedRowsTarget.appendChild(this.bookedRowFor(piece));
      else this.traceRowsTarget.appendChild(this.traceRowFor(piece));
    }
    this.afterPick();
  }

  pickKind(kind) {
    if (this.kindIds().includes(String(kind.id))) return;
    this.choiceRowsTarget.appendChild(this.kindRowFor(kind));
    this.afterPick();
  }

  afterPick() {
    this.activeQuery().value = '';
    this.syncEmpty();
    this.fetchResults();
  }

  // ----- the rows -----

  traceRowFor(piece, cal = null) {
    const row = this.traceTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = piece.id;
    this.fillName(row, piece);
    row.querySelector('input[type="hidden"]').value = piece.id;
    const book = row.querySelector('.te-book');
    if (book) book.hidden = !piece.bookable;
    const cell = row.querySelector('.te-cal');
    if (cell) this.fillCal(cell, piece, cal);
    return row;
  }

  bookedRowFor(piece, cal = null) {
    const row = this.bookedTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = piece.id;
    this.fillName(row, piece);
    row.querySelector('.te-name').title = [piece.category, piece.location].filter(Boolean).join(' · ');
    row.querySelector('.te-booked-id').value = piece.id;
    const cell = row.querySelector('.te-cal');
    if (cell) this.fillCal(cell, piece, cal);
    return row;
  }

  choiceRowFor(piece) {
    const row = this.choiceTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = piece.id;
    this.fillName(row, piece);
    row.querySelector('input[type="hidden"]').value = piece.id;
    const cell = row.querySelector('.te-cal');
    if (cell && !this.template) {
      cell.innerHTML = '';
      cell.appendChild(this.el('small', piece.cal_bad ? 'te-bad' : '', `now ${piece.cal}`));
    }
    return row;
  }

  kindRowFor(kind) {
    const row = this.kindTemplateTarget.content.firstElementChild.cloneNode(true);
    row.dataset.id = `kind-${kind.id}`;
    row.querySelector('.te-n').textContent = `Every ${kind.name}`;
    row.querySelector('.te-name small').textContent = `${this.plural(kind.count, 'piece')} on the Schedule`;
    row.querySelector('input[type="hidden"]').value = kind.id;
    return row;
  }

  // The piece's Identifier and name, and (where the row has one) its
  // category and location under them
  fillName(row, piece) {
    const ident = row.querySelector('.te-ident');
    if (ident) ident.textContent = piece.ident || '';
    row.querySelector('.te-n').textContent = piece.name;
    const note = row.querySelector('.te-name > small');
    if (note) note.textContent = [piece.category, piece.location].filter(Boolean).join(' · ');
  }

  removeTrace(event) {
    event.currentTarget.closest('.te-row').remove();
    this.syncEmpty();
  }

  removeBooked(event) {
    event.currentTarget.closest('.te-row').remove();
    this.syncEmpty();
  }

  removeChoice(event) {
    event.currentTarget.closest('.te-row').remove();
    this.syncEmpty();
  }

  // Holds / Can Share on one This Equipment row
  bookedShare(event) {
    const button = event.currentTarget;
    const row = button.closest('.te-row');
    row.querySelector('.te-booked-share').value = button.dataset.value;
    button.parentElement.querySelectorAll('button').forEach((b) => {
      b.classList.toggle('is-on', b === button);
      b.setAttribute('aria-pressed', String(b === button));
    });
  }

  // ----- between the sections -----

  // A booking stops booking time; it stays on the record.
  moveToTrace(event) {
    const row = event.currentTarget.closest('.te-row');
    const id = row && row.dataset.id;
    if (!id) return;
    const piece = this.pieces[id] || this.pieceFromRow(row);
    this.openTrace();
    this.traceRowsTarget.appendChild(this.traceRowFor(piece, row.querySelector('.te-cal')));
    row.remove();
    this.syncEmpty();
  }

  // A Traceability Only piece that can be booked goes on the Schedule as This
  // Equipment (Holds); it stays on the record.
  bookIt(event) {
    const traceRow = event.currentTarget.closest('.te-row');
    const id = traceRow.dataset.id;
    if (!id || !this.hasBookedTemplateTarget || this.equipmentMode() === 'any') return;
    if (this.equipmentMode() === 'none') this.pressMode('this');
    const piece = this.pieces[id] || this.pieceFromRow(traceRow);
    this.bookedRowsTarget.appendChild(this.bookedRowFor(piece, traceRow.querySelector('.te-cal')));
    traceRow.remove();
    this.syncEmpty();
  }

  // An empty Traceability Only, folded to one line, opens to its search
  openTrace() {
    if (!this.hasTraceSectionTarget || !this.traceSectionTarget.hidden) return;
    this.traceSectionTarget.hidden = false;
    if (this.hasTraceFoldTarget) this.traceFoldTarget.hidden = true;
    const query = this.queryTargets.find((q) => q.dataset.for === 'trace');
    if (query) query.focus();
  }

  // ----- helpers -----

  // The calibration column for a piece the window adds: moved with its row
  // (from), else "When it starts, now cal. due Dec 3", or for a task that
  // has started "On save, recorded with cal. due Dec 3".
  fillCal(cell, piece, from = null) {
    cell.innerHTML = '';
    if (from && from.childNodes.length) {
      this.moveChildren(from, cell);
      return;
    }
    if (this.template || !piece.cal) return;
    cell.appendChild(this.el('span', 'te-pend', this.started ? 'On save' : 'When it starts'));
    const note = this.el('small', piece.cal_bad ? 'te-bad' : '', this.started ? `recorded with ${piece.cal}` : `now ${piece.cal}`);
    cell.appendChild(note);
  }

  moveChildren(from, to) {
    to.innerHTML = '';
    while (from.firstChild) to.appendChild(from.firstChild);
  }

  pieceFromRow(row) {
    const ident = row.querySelector('.te-ident');
    const name = row.querySelector('.te-n');
    return { id: row.dataset.id, name: name ? name.textContent : row.dataset.id, ident: ident ? ident.textContent : null, bookable: true, cal: '', cal_bad: false };
  }

  // The pieces on the window: Traceability Only and This Equipment
  currentIds() {
    const ids = this.traceRowTargets.map((row) => row.dataset.id).concat(this.bookedRowTargets.map((row) => row.dataset.id));
    return ids.filter(Boolean).map(String);
  }

  choiceIds() {
    return this.choiceRowTargets.map((row) => row.dataset.id).filter((id) => id && !id.startsWith('kind-')).map(String);
  }

  kindIds() {
    return this.choiceRowTargets.map((row) => row.dataset.id).filter((id) => id && id.startsWith('kind-')).map((id) => id.slice(5));
  }

  twoSections() {
    return !!this.element.querySelector('[data-target~="task-scheduling.equipmentMode"]');
  }

  equipmentMode() {
    const input = this.element.querySelector('[data-target~="task-scheduling.equipmentMode"]');
    return input ? input.value : null;
  }

  pressMode(value) {
    const button = this.element.querySelector(`button[data-group="equipment"][data-value="${value}"]`);
    if (button) button.click();
  }

  // Book It shows only while the bookings are This Equipment or Nothing to Book.
  modeClicked(event) {
    if (event.target.closest('button[data-group="equipment"]')) this.syncMode();
  }

  syncMode() {
    const mode = this.equipmentMode();
    if (mode) this.element.dataset.equipmentMode = mode;
  }

  syncEmpty() {
    if (this.hasTraceEmptyTarget) this.traceEmptyTarget.hidden = this.traceRowTargets.length > 0;
    if (this.hasBookedEmptyTarget) this.bookedEmptyTarget.hidden = this.bookedRowTargets.length > 0;
    if (this.hasChoiceEmptyTarget) this.choiceEmptyTarget.hidden = this.choiceRowTargets.length > 0;
  }

  // The name typed on a new task's form, for "usually used on" (a saved task is found by its id).
  taskName() {
    const form = this.element.closest('form');
    const field = form && form.querySelector('input[name="task[name]"]');
    return field ? field.value.trim() : '';
  }

  plural(n, word) {
    return `${n} ${word}${n === 1 ? '' : 's'}`;
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
}
